<template>
  <div class="row">
    <language-selector />
    <card-update-page :inProgress="inProgress" />
    <card-update-videos v-if="['video'].indexOf(page.type) !== -1" />
    <card-update-card-grid v-if="['card-grid'].indexOf(page.type) !== -1" />

    <div v-if="['place'].indexOf(page.type) !== -1" class="card-update-tags">
      <card>
        <template #header>
          <div class="title-row">
            <h4 class="card-title mb-0">Edit Tags</h4>

            <div class="info-icon-wrapper">
              <div class="edit-tags-info">
                <InfoBox>
                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Basics</div>
                    <ul>
                      <li>
                        Click <strong>Add Tag</strong> to create a new tag.
                      </li>
                      <li>Drag tags to reposition them on the map.</li>
                      <li>
                        Click a tag to select it;
                        <strong>double-click</strong> to open the Edit Menu (pen
                        icon) or click the icon itself.
                      </li>
                    </ul>
                  </div>

                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Toolbar</div>
                    <ul>
                      <li>
                        Use the left toolbar for <strong>Zoom</strong>,
                        <strong>Undo / Redo</strong> and
                        <strong>Alignment</strong> tools.
                      </li>
                      <li>Selected tags can be aligned or moved together.</li>
                    </ul>
                  </div>

                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Shortcuts</div>
                    <ul>
                      <li>Hold <strong>Shift</strong> to multi-select tags.</li>
                      <li>
                        <strong>Shift + Double-Click</strong> a tag to select
                        all tags of that type.
                      </li>
                      <li>
                        Use <strong>Shift +</strong> to zoom in and
                        <strong>Shift -</strong> to zoom out. Use
                        <strong>Shift 0</strong> to reset to default view.
                      </li>
                      <li>
                        Press <strong>Delete</strong> or
                        <strong>Backspace</strong> to remove a selected
                        tag/element
                      </li>
                      <li>
                        Press <strong>Esc</strong> to clear selection or exit
                        edit mode.
                      </li>
                      <li>
                        Use <strong>Arrow Keys</strong> to adjust position for
                        'Pixel Perfect' placement.
                      </li>
                    </ul>
                  </div>
                </InfoBox>
              </div>
            </div>
          </div>
        </template>

        <div class="d-flex flex-wrap align-items-center my-2">
          <button
            v-if="!activeSlide"
            class="btn btn-primary mr-2 mb-2"
            @click="addTag"
          >
            Add Tag
          </button>

          <button
            v-if="activeSlide"
            class="btn btn-success mr-2 mb-2"
            @click="addTagToSlide"
          >
            Add Tag to Slide
          </button>

          <button class="btn btn-success mb-2 mr-3" @click="addSlide">
            Add Slide
          </button>

          <!-- reference slider orientation toggle -->
          <div
            v-if="referenceTagsSlider.length > 0"
            class="mb-2 d-flex flex-column reference-toggle-wrapper"
          >
            <label class="mb-0 reference-toggle-label">
              Reference Slider Orientation
            </label>

            <toggle-button
              v-model="page.verticalSlider"
              :color="{ checked: '#28a745', unchecked: '#28a745' }"
              :sync="true"
              :labels="{ checked: 'Vertical', unchecked: 'Horizontal' }"
              :width="100"
              :height="30"
            />
          </div>
        </div>

        <DraggableElements
          :tags="page.tags"
          :background-image="page.backgroundImage.en"
          @remove-slide="handleRemoveSlide"
        />
      </card>
    </div>

    <div class="col-12">
      <button
        :disabled="inProgress"
        @click="updatePage(page._id)"
        class="btn btn-primary"
        type="button"
      >
        Update Page
      </button>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import store from "@/store";
import { FETCH_ASSET, FETCH_PAGE, PAGE_EDIT } from "@/store/actions.type";
import InfoBox from "@/components/InfoBox";
import CkEditor from "@/components/CKEditor";
import CardUpdatePage from "@/pages/PageEdit/CardUpdatePage";
import CardUpdateVideos from "@/pages/PageEdit/CardUpdateVideos";
import CardUpdateCardGrid from "@/pages/PageEdit/CardUpdateCardGrid";
import DraggableElements from "@/components/DraggableElements.vue";

export default {
  name: "PagesView",

  components: {
    CardUpdatePage,
    CardUpdateVideos,
    CkEditor,
    CardUpdateCardGrid,
    DraggableElements,
    InfoBox
  },

  data() {
    return {
      initialPageJSON: null,
      inProgress: false
    };
  },

  computed: {
    ...mapGetters({
      langs: "langs",
      page: "page",
      activeElement: "draggable/getActiveElement",
      getActiveSlide: "draggable/getActiveSlide"
    }),

    activeSlide: {
      get: function() {
        return this.getActiveSlide;
      },

      set: function() {}
    },

    referenceTagsSlider() {
      const baseRefs = (this.page.tags || []).filter(
        tag => tag.type === "reference" && !tag.hideTagReference
      );

      const slideRefs = (this.page.interactiveSlides || [])
        .flatMap(slide => slide.tags || [])
        .filter(tag => tag.type === "reference" && !tag.hideTagReference);

      return [...baseRefs, ...slideRefs];
    },

    currentPageJSON() {
      return JSON.stringify(this.page);
    },

    isPageChanged() {
      return this.currentPageJSON !== this.initialPageJSON;
    },

    highestId() {
      let ids = [0];

      this.page.tags.forEach(tag => {
        if (!isNaN(tag._id)) {
          ids.push(tag._id);
        }
      });

      this.page.interactiveSlides.forEach(slide => {
        if (!isNaN(slide._id)) {
          ids.push(slide._id);
        }
      });

      this.page.interactiveSlides.forEach(slide => {
        slide.tags.forEach(tag => {
          if (!isNaN(tag._id)) {
            ids.push(tag._id);
          }
        });
      });

      return (Math.max(...ids) + 1).toString();
    }
  },

  beforeRouteEnter(to, from, next) {
    Promise.all([
      store.dispatch(FETCH_PAGE, to.params.id),
      store.dispatch("FETCH_ASSETS")
    ]).then(() => {
      next();
    });
  },

  beforeRouteLeave(to, from, next) {
    if (this.isPageChanged) {
      if (
        window.confirm("Warning! Unsaved changes. Do you really want to leave?")
      ) {
        next();
      }
    } else {
      next();
    }
  },

  mounted() {
    this.initialPageJSON = JSON.stringify(this.page);
  },

  methods: {
    addTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then(res => {
        this.page.tags.push(res.data);
      });
    },

    async addTagToSlide() {
      let slide = this.page.interactiveSlides.find(
        slide => slide._id === this.activeSlide._id
      );

      let { data } = await this.$store.dispatch("GET_TAG_SCHEMA");
      data["elementType"] = "slidetag";
      slide.tags.push(data);
    },

    addSlide() {
      const newSlide = {
        _id: this.highestId,
        asset: { isEmpty: true },
        assetId: null,
        backgroundImageOverride: "",
        label: {
          en: ""
        },
        tags: [],
        elementType: "slide"
      };

      this.page.interactiveSlides.push(newSlide);
    },

    handleRemoveSlide(slideId) {
      if (!Array.isArray(this.page.interactiveSlides)) return;

      const index = this.page.interactiveSlides.findIndex(
        slide => slide._id === slideId
      );

      if (index === -1) return;

      // remove the slide
      this.page.interactiveSlides.splice(index, 1);

      // if that slide was active in the draggable store, clear it
      if (this.getActiveSlide && this.getActiveSlide._id === slideId) {
        this.$store.commit("draggable/setActiveSlide", null);
        this.$store.commit("draggable/deselectAll");
      }
    },

    // activeSlide(slide) {
    //   this.selectedSlide = slide;
    // },

    updatePage(id) {
      this.inProgress = true;
      this.$store.dispatch(PAGE_EDIT, this.page).finally(() => {
        this.inProgress = false;
        this.$store.dispatch(FETCH_PAGE, this.page._id).then(() => {
          this.initialPageJSON = JSON.stringify(this.page);
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.card-update-tags {
  position: relative;
  width: 100%;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-icon-wrapper {
  display: flex;
  align-items: center;
  transform: translateY(3px);
  z-index: 5000;
}

.edit-tags-info {
  max-width: 380px;
  &__section {
    &:not(:last-child) {
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    ul {
      margin: 4px 0 0;
      padding-left: 18px;
      font-size: 13px;
      line-height: 1.45;
    }
  }

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #7a7a85;
    font-weight: 600;
  }
}

.reference-toggle-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0px;
}

.reference-toggle-label {
  margin-bottom: 0;
}
</style>
